import React, { useEffect, useRef, useState } from "react";
import Peer from "peerjs";
import io from "socket.io-client";
import {
  Box,
  Tooltip,
  IconButton,
  Typography,
} from "@mui/material";
import FlipCameraAndroidIcon from "@mui/icons-material/FlipCameraAndroid";
import { useParams, useLocation } from "react-router-dom";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";

import RefreshIcon from "@mui/icons-material/Refresh";

export default function Insured() {
  const videoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const currentCallRef = useRef(null);
  const connRef = useRef(null);
  const socketRef = useRef(null);

  const { aid } = useParams();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");

  const [peerId, setPeerId] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [isFrontCamera, setIsFrontCamera] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [sessionValid, setSessionValid] = useState(true);

  const [locationData, setLocationData] = useState(null);

  const [isReconnecting, setIsReconnecting] = useState(false);
  const reconnectRef = useRef(false);

  const clientId = localStorage.getItem("clientId") || crypto.randomUUID();
  localStorage.setItem("clientId", clientId);

  /* ---------------- SOCKET ---------------- */
  useEffect(() => {
    if (!aid) return;

    const socket = io("https://live.claimsmitra.com", {
      path: "/socket.io",
      reconnectionAttempts: Infinity,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      transports: ["websocket"],
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      setIsReconnecting(false);
      socket.emit("join-room", {
        roomId: aid,
        role: "insured",
        clientId,
      });
    });

    socket.on("disconnect", (reason) => {

      console.log(
        "❌ INSURED SOCKET DISCONNECTED:",
        reason
      );

      setIsReconnecting(true);
    });

    socket.io.on("reconnect_attempt", () => {

      console.log("🔄 INSURED RECONNECTING...");

      setIsReconnecting(true);
    });

    socket.io.on("reconnect", () => {

      console.log("✅ INSURED RECONNECTED");

      setIsReconnecting(false);

      socket.emit("join-room", {
        roomId: aid,
        role: "insured",
        clientId,
      });
    });

    socket.on("survey-completed", () => {

      alert("Survey completed by surveyor.");

      // STOP CAMERA
      if (localStream) {
        localStream.getTracks().forEach((track) => track.stop());
      }

      // CLOSE PAGE OR REDIRECT
      window.location.href = "/completed";
    });

    return () => socket.disconnect();
  }, [aid]);

  useEffect(() => {
    reconnectRef.current = isReconnecting;
  }, [isReconnecting]);


  useEffect(() => {
    if (!aid || !socketRef.current) return;

    if (!navigator.geolocation) {
      console.log("Geolocation not supported");
      return;
    }

    const watchId = navigator.geolocation.watchPosition(
      (position) => {

        const payload = {
          roomId: aid,
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          time: new Date().toLocaleString('en-IN', {
            timeZone: 'Asia/Kolkata'
          }),
        };

        console.log("📤 Sending insured location:", payload);

        socketRef.current.emit("insured-location", payload);

      },
      (error) => {
        console.log("GPS ERROR:", error);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      }
    );

    return () => {
      navigator.geolocation.clearWatch(watchId);
    };

  }, [aid]);

  /* ---------------- SESSION ---------------- */
  // useEffect(() => {
  //   if (!aid || !token) return;

  //     fetch(
  //       `https://claimsmitra.com/livesurvey/join/${aid}?token=${token}&client_id=${clientId}`
  //     )
  //     .then((res) => res.json())
  //     .then((res) => {
  //       if (!res.status) {
  //         alert(res.msg || "Session invalid");
  //         window.location.href = "/expired";
  //       } else {
  //         setSessionValid(true);
  //       }
  //     })
  //     .catch(() => {
  //       alert("Server error");
  //     });
  // }, [aid, token]);

  useEffect(() => {
    // 🔥 BYPASSED SESSION VALIDATION TEMP
    setSessionValid(true);
  }, []);

  /* ---------------- ATTACH STREAM SAFELY ---------------- */
  const attachStream = (videoEl, stream) => {
    if (!videoEl || !stream) return;

    videoEl.srcObject = stream;

    videoEl.onloadedmetadata = () => {
      videoEl.play().catch(() => {
        console.warn("Autoplay blocked, retrying...");
        setTimeout(() => videoEl.play().catch(() => {}), 500);
      });
    };
  };

  /* ---------------- CAMERA + PEER ---------------- */
  useEffect(() => {
    if (!aid || !sessionValid) return;

    let peerInstance;

    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: isFrontCamera
            ? { ideal: "user" }
            : { ideal: "environment" },
          },
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });

        setLocalStream(stream);

        // ✅ FORCE REATTACH AFTER RENDER
        setTimeout(() => {
          attachStream(videoRef.current, stream);
        }, 100);

        peerInstance = new Peer({
          host: "live.claimsmitra.com",
          path: "/peerjs",
          secure: true,
          config: {
            iceServers: [
              { urls: "stun:stun.l.google.com:19302" },
              {
                urls: [
                  "turn:187.127.154.93:3478?transport=udp",
                  "turn:187.127.154.93:3478?transport=tcp",
                  "turns:187.127.154.93:5349?transport=tcp",
                ],
                username: "username1",
                credential: "password1",
              },
            ],
          },
        });

        peerInstance.on("open", (id) => {

          setIsReconnecting(false);
          setPeerId(id);

          if (currentCallRef.current) {
            currentCallRef.current.close();
          }

          setTimeout(() => {
            if (!peerInstance || !stream) return;

            const call = peerInstance.call(aid, stream);
            currentCallRef.current = call;

            const conn = peerInstance.connect(aid);
            connRef.current = conn;

            call.on("stream", (remoteStream) => {
              // ✅ IMPORTANT FIX
              setTimeout(() => {
                attachStream(remoteVideoRef.current, remoteStream);
                remoteVideoRef.current.volume = 1;
                remoteVideoRef.current.play().catch((e) => {
                  console.log("Play failed:", e);
                });
              }, 100);
            });

            call.on("error", (err) => {
              console.log("❌ Call error:", err);
            });
          }, 500);
        });


        // peerInstance.on("call", (incomingCall) => {

        //   console.log("📞 Incoming call");

        //   // CLOSE OLD CALL
        //   if (currentCallRef.current) {
        //     currentCallRef.current.close();
        //   }

        //   currentCallRef.current = incomingCall;

        //   // ANSWER CALL
        //   incomingCall.answer(stream);

        //   incomingCall.on("stream", (remoteStream) => {

        //     console.log("🎵 Remote stream received");

        //     // ENABLE AUDIO TRACKS
        //     remoteStream.getAudioTracks().forEach(track => {
        //       track.enabled = true;
        //     });

        //     if (remoteVideoRef.current) {

        //       remoteVideoRef.current.srcObject = remoteStream;

        //       remoteVideoRef.current.muted = false;

        //       remoteVideoRef.current.volume = 1;

        //       remoteVideoRef.current.onloadedmetadata = async () => {

        //         try {

        //           await remoteVideoRef.current.play();

        //           console.log("▶️ Remote audio playing");

        //         } catch (err) {

        //           console.log("❌ Play failed:", err);
        //         }
        //       };
        //     }
        //   });

        //   incomingCall.on("close", () => {
        //     console.log("❌ Incoming call closed");
        //   });

        //   incomingCall.on("error", (err) => {
        //     console.log("❌ Incoming call error:", err);
        //   });

        // });

        peerInstance.on("disconnected", () => {

          console.log("❌ Peer disconnected");

          setIsReconnecting(true);

          peerInstance.reconnect();
        });

        peerInstance.on("close", () => {

          console.log("❌ Peer closed");

          setIsReconnecting(true);
        });

        peerInstance.on("error", (err) => {

          console.log("❌ Peer error:", err);

          setIsReconnecting(true);
        });

      } catch (err) {
        console.error("Media error:", err);
      }
    };

    start();

    return () => {
      connRef.current?.close();
      currentCallRef.current?.close();
      peerInstance?.destroy();
      localStream?.getTracks().forEach((t) => t.stop());
    };
  }, [aid, sessionValid]);

  /* ---------------- UI ---------------- */
  if (!sessionValid) {
    return (
      <div style={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        background: "#000"
      }}>
        Connecting to session...
      </div>
    );
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "#000" }}>
      {/* 🎥 LOCAL VIDEO */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          transform: isFrontCamera ? "scaleX(-1)" : "scaleX(1)",
        }}
      />

      {/* 🎥 REMOTE (SURVEYOR) */}
      <Box
        sx={{
          position: "absolute",
          bottom: 16,
          right: 16,
          width: 140,
          height: 180,
          zIndex: 5,
        }}
      >
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          controls={false}
          style={{
            width: "100%",
            height: "100%",
            borderRadius: 10,
            objectFit: "cover",
            border: "2px solid #fff",
            background: "#000",
          }}
        />
      </Box>

      {/* 🔇 MUTE */}
      {/* 🔄 PAGE RELOAD */}
      <Box sx={{ position: "absolute", top: 8, left: 8, zIndex: 10,}}>
        <Tooltip title="Reload Page">
          <IconButton
            onClick={() => {
              window.location.reload();
            }}
            sx={{ color: "#fff" }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* 🔄 SWITCH CAMERA */}
      <Box
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 10,
        }}
      >
        <Tooltip title="Switch Camera">
          <IconButton
            disabled={isReconnecting}
            sx={{
              color: "#fff",
              background: "rgba(0,0,0,0.5)",
              "&:hover": {
                background: "rgba(0,0,0,0.7)",
              },
            }}
            onClick={async () => {

              const nextFront = !isFrontCamera;

              try {

                // GET NEW MEDIA STREAM
                const newStream = await navigator.mediaDevices.getUserMedia({
                  video: {
                    facingMode: nextFront
                      ? { exact: "user" }
                      : { exact: "environment" },
                  },
                  audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                  },
                });

                const newVideoTrack = newStream.getVideoTracks()[0];
                const newAudioTrack = newStream.getAudioTracks()[0];

                // PRESERVE MUTE STATE
                if (newAudioTrack) {
                  newAudioTrack.enabled = !isMuted;
                }

                // STOP OLD TRACKS
                if (localStream) {
                  localStream.getTracks().forEach(track => {
                    track.stop();
                  });
                }

                // UPDATE LOCAL STATE
                setLocalStream(newStream);
                setIsFrontCamera(nextFront);

                // UPDATE LOCAL VIDEO
                if (videoRef.current) {
                  videoRef.current.srcObject = newStream;

                  await videoRef.current.play().catch(() => {});
                }

                // REPLACE TRACKS
                const pc = currentCallRef.current?.peerConnection;

                if (pc) {

                  const senders = pc.getSenders();

                  const videoSender = senders.find(
                    sender => sender.track?.kind === "video"
                  );

                  const audioSender = senders.find(
                    sender => sender.track?.kind === "audio"
                  );

                  if (videoSender && newVideoTrack) {
                    await videoSender.replaceTrack(newVideoTrack);
                  }

                  if (audioSender && newAudioTrack) {
                    await audioSender.replaceTrack(newAudioTrack);
                  }
                }

                console.log("✅ Camera switched");

              } catch (err) {

                console.error("❌ Camera switch error:", err);

                // FALLBACK
                try {

                  const fallbackStream =
                    await navigator.mediaDevices.getUserMedia({
                      video: {
                        facingMode: nextFront
                          ? "user"
                          : "environment",
                      },
                      audio: {
                        echoCancellation: true,
                        noiseSuppression: true,
                        autoGainControl: true,
                      },
                    });

                  setLocalStream(fallbackStream);
                  setIsFrontCamera(nextFront);

                  if (videoRef.current) {
                    videoRef.current.srcObject = fallbackStream;
                  }

                } catch (fallbackErr) {

                  console.error(
                    "❌ Fallback camera failed:",
                    fallbackErr
                  );
                }
              }
            }}
          >
            <FlipCameraAndroidIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* RECONNECTING OVERLAY */}
      {isReconnecting && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.75)",
            zIndex: 99999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            color: "#fff",
            backdropFilter: "blur(4px)",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 2,
            }}
          >
            Reconnecting...
          </Typography>

          <Typography variant="body1">
            Network issue detected.
            Trying to reconnect.
          </Typography>
        </Box>
      )}
    </div>
  );
}